import React, { useState, useContext, useEffect } from "react";
import { HStack, IconButton, Badge, Stack } from "@chakra-ui/react";
import { FiEdit2, FiTrash2, FiPlus, FiBook, FiLayers } from "react-icons/fi";
import { useNavigate  } from "react-router-dom";
import { AppContext } from "../../App";
import { EApiContactSource, IApiContactResponse } from "../../models/api/contact/IApiContactResponse";
import { ApiContext } from "../../contexts/ApiProvider";
import toast from "react-hot-toast";
import { AddContactDrawer } from "../../components/drawers/contact/AddContactDrawer";
import { EditContactDrawer } from "../../components/drawers/contact/EditContactDrawer";
import { IApiContactEditRequest } from "../../models/api/contact/IApiContactEditRequest";
import { ContactSearchResult } from "../../components/ContactSearch/ContactSearchResult";
import { AlgoliaTable } from "../../components/AlgoliaTable/AlgoliaTable";
import Helpers from "../../Helpers";

interface IContactsProps {

}

export const Contacts: React.FunctionComponent<IContactsProps> = (props: IContactsProps) => {

    const navigate = useNavigate();
    const { me, setAppShellConfig } = useContext(AppContext);
    const { postContact, patchContact, deleteContact } = useContext(ApiContext);

    const addState = useState(false);
    const [add, setAdd] = addState;
    
    useEffect(() => {
        setAppShellConfig({
            breadcrumbs: [{
                text: `Vorlagen`,
                icon: FiLayers,
            },
            {
                text: `Kontakte`,
                icon: FiBook
            }],
            rightButton: {
                text: "Erstellen",
                icon: FiPlus,
                ariaLabel: "erstellen",
                variant: "primary",
                onClick: () => {
                    setAdd(true);
                }
            }
        });
    }, [setAppShellConfig, me, navigate, setAdd]);

    return (
        <Stack spacing="4" height="full" direction="column">
            <AlgoliaTable<IApiContactResponse> 
                indexName="dev_contacts"
                getColumns={(columnHelper, setEdit, deleteAction) => [
                    columnHelper.accessor('name', {
                        header: () => 'Firma / Anzeigename',
                        cell: info => info.getValue()
                    }),
                    columnHelper.accessor('firstName', {
                        header: () => 'Vorname',
                        cell: info => info.getValue()
                    }),
                    columnHelper.accessor('lastName', {
                        header: () => 'Nachname',
                        cell: info => info.getValue()
                    }),
                    columnHelper.accessor('email', {
                        header: () => 'E-Mail',
                        cell: info => info.getValue()
                    }),
                    columnHelper.accessor('sourceType', {
                        header: () => 'Quelle',
                        cell: info => ([
                            <Badge colorScheme={'blue'} size={{ base: "xs", sm: "sm" }}>uStrich</Badge>,
                            <Badge colorScheme={'yellow'} size={{ base: "xs", sm: "sm" }}>AD</Badge>,
                            <Badge colorScheme={'purple'} size={{ base: "xs", sm: "sm" }}>SharePoint</Badge>,
                            <Badge colorScheme={'green'} size={{ base: "xs", sm: "sm" }}>BauKG</Badge>,
                            <Badge colorScheme={'orange'} size={{ base: "xs", sm: "sm" }}>Benutzer</Badge>,
                            <Badge colorScheme={'gray'} size={{ base: "xs", sm: "sm" }}>N/A</Badge>
                        ][info.getValue()])
                    }),
                    columnHelper.display({
                        id: 'actions',
                        cell: info => {
                            const contact = info.row.original;
                            return (
                                <HStack spacing="0" justifyContent="end">
                                    {contact.sourceType === EApiContactSource.BauKG && (
                                        <>
                                            <IconButton
                                                icon={<FiEdit2 fontSize="1.125rem" />}
                                                variant="ghost"
                                                aria-label="Bearbeiten"
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    setEdit(contact);
                                                }}
                                                isDisabled={contact.sourceType !== EApiContactSource.BauKG}
                                            />
                                            <IconButton
                                                icon={<FiTrash2 fontSize="1.125rem" />}
                                                variant="ghost"
                                                aria-label="Löschen"
                                                onClick={(e) => { 
                                                    e.stopPropagation();
                                                    deleteAction(contact);
                                                }}
                                                isDisabled={contact.sourceType !== EApiContactSource.BauKG}
                                            />  
                                        </>
                                    )}                         
                                </HStack>
                            );
                        },
                    }),
                ]}
                getCard={(contact, setEdit, deleteAction) => ({
                    key: contact.id,
                    value: contact.id,
                    content: (
                        <HStack spacing="4" justifyContent="space-between">
                            <ContactSearchResult contact={contact} />
                            <HStack spacing="4" justifyContent="end">
                                {contact.sourceType === EApiContactSource.BauKG && (
                                    <>
                                        <IconButton
                                            icon={<FiEdit2 fontSize="1.25rem" />}
                                            variant="ghost"
                                            aria-label="Bearbeiten"
                                            onClick={(e) => { 
                                                e.stopPropagation(); 
                                                setEdit(contact);
                                            }}
                                            isDisabled={contact.sourceType !== EApiContactSource.BauKG}
                                        />
                                        <IconButton
                                            icon={<FiTrash2 fontSize="1.25rem" />}
                                            variant="ghost"
                                            aria-label="Löschen"
                                            onClick={(e) => { 
                                                e.stopPropagation(); 
                                                deleteAction(contact);
                                            }}
                                            isDisabled={contact.sourceType !== EApiContactSource.BauKG}
                                        />
                                    </>
                                )}
                            </HStack>
                        </HStack>
                    )
                })}
                deleteAction={(item, deleteItem) => ({
                    title: `Kontakt löschen`,
                    body: `Sind Sie sicher, dass dieser Kontakt gelöscht werden soll?`,
                    onConfirm: () => {
                        var promise = deleteContact(item.id);
                        toast.promise(promise, {
                            loading: `Kontakt wird gelöscht...`,
                            success: (result) => {
                                deleteItem(result);
                                return `Der Kontakt wurde erfolgreich gelöscht!`;
                            },
                            error: (code) => Helpers.buildToastErrorMessage(code, `Fehler beim Löschen des Kontakts!`)
                        });
                        return promise;
                    }
                })}
                addState={addState}
                getAddDrawer={([state, setState], addItem) => (
                    <AddContactDrawer 
                        isOpen={state} 
                        onDiscard={() => setState(false)}
                        onSubmit={(data) => {
                            var promise = postContact(data);
                            toast.promise(promise, {
                                loading: `Kontakt wird erstellt...`,
                                success: (result) => {
                                    setState(false);
                                    addItem(result);
                                    return `Der Kontakt wurde erfolgreich erstellt!`;
                                },
                                error: (code) => Helpers.buildToastErrorMessage(code, `Fehler beim Erstellen des Kontakts!`)
                            });
                            return promise;
                        }}
                    />
                )}
                getEditDrawer={([state, setState], replaceItem) => (
                    <EditContactDrawer 
                        defaultValue={state as IApiContactEditRequest} 
                        onDiscard={() => setState(undefined)} 
                        onSubmit={(data) => {
                            var promise = patchContact(state!.id, data);
                            toast.promise(promise, {
                                loading: `Kontakt wird aktualisiert...`,
                                success: (result) => {
                                    setState(undefined);
                                    replaceItem(result)
                                    return `Der Kontakt wurde erfolgreich aktualisiert!`;
                                },
                                error: (code) => Helpers.buildToastErrorMessage(code, `Fehler beim Aktualisieren des Kontakts!`)
                            });
                            return promise;
                        }}
                    />
                )}
            />
        </Stack>
    );

}